import React, { useCallback } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';

// Material UI
import { Box } from '@material-ui/core';

// Data
import { ASSESSMENT_SESSION_SUMMARY_FIELDS } from '../../../lib/queries/assessment-session.queries';
import { COMMUNICATION_FIELDS } from '../../../lib/queries/email-log.queries';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import { PrimaryButton } from '../../_core/_ui/buttons.component';
import { getAuthUser } from '../../../lib/services/auth.service';
import { SectionWrapper } from '../../_core/_ui/structure.components';
import { SectionHeading } from '../../_core/_ui/typography.component';
import { CommunicationList } from './email-log-list.component';
// import { CommunicationFilter } from './email-log-list-filter.component'; // Hidden for now - scoped to player assessment


const GET_COMMUNICATION_LIST = gql`
  query GetCommunicationList($playerAssessmentId: ID!, $status: [String!]) {
    communications(playerAssessmentId: $playerAssessmentId, status: $status) {
      ...CommunicationFields
      assessmentSession {
        ...AssessmentSessionSummaryFields
      }
    }
  }

  ${COMMUNICATION_FIELDS}
  ${ASSESSMENT_SESSION_SUMMARY_FIELDS}
`;

const RESEND_COMMUNICATION = gql`
  mutation ResendCommunication($communicationId: ID!) {
    resendCommunication(communicationId: $communicationId)
  }
`;

interface ICommunicationListGraphQL {
  playerAssessmentId: string;
}

export const CommunicationListSection: React.FC<ICommunicationListGraphQL> = ({ playerAssessmentId }) => {
  const user = getAuthUser();

  const terminology = useCompanySportTerminology()
  const athlete = terminology[SportTerminology.ATHLETE].toLowerCase()

  const { data, loading, refetch } = useQuery(GET_COMMUNICATION_LIST, {
    variables: { playerAssessmentId, status: ["Success", "Failed", "Failed - Timed Out", "Pending"] as CommunicationResult[] },
    fetchPolicy: 'cache-and-network'
  });

  const [resendCommunication] = useMutation(RESEND_COMMUNICATION)

  // The outcome the send settled on — CommunicationResult, or "In Progress"
  // when the row is already claimed. Never rejects, so batches can tally.
  const resendOne = useCallback((communicationId: string): Promise<ResendOutcome> => resendCommunication({
    variables: { communicationId },
  })
    .then(({ data: result }) => (result?.resendCommunication || "Failed") as ResendOutcome)
    .catch((err) => {
      console.error('resendCommunication failed', communicationId, err);
      return "Failed" as ResendOutcome;
    }), [resendCommunication])

  // The row's status and sentAt are settled by the send itself, so the list is
  // re-read rather than guessed at — a failed resend must not render as Pending.
  const refresh = useCallback(() => {
    refetch().catch((err) => console.error('Failed to refresh communications after resend', err));
  }, [refetch])

  const handleResendCommunication = useCallback(
    (communicationId: string) => resendOne(communicationId).then((outcome) => { refresh(); return outcome; }),
    [resendOne, refresh]
  )

  const handleResendAll = useCallback(async () => {
    // Rows whose recipient has left the player's contacts are refused by the
    // server, so including them would just tally as failures.
    const communications: ICommunication[] = (data?.communications || [])
      .filter((c: ICommunication) => c.hasActiveRecipient !== false);
    const outcomes = await Promise.all(communications.map((c) => resendOne(c._id)));
    refresh();

    const sent = outcomes.filter((o) => o === "Success").length;
    const skipped = outcomes.filter((o) => o === "Skipped").length;
    if (!outcomes.length) alert("There are no messages to resend.");
    else if (sent === outcomes.length) alert(`Resent ${sent} ${sent === 1 ? 'message' : 'messages'}.`);
    else alert(`Resent ${sent} of ${outcomes.length} messages.${skipped ? ` ${skipped} had no reachable contact.` : ''} Check each row's status for the rest.`);
  }, [data, resendOne, refresh])

  return (
    <SectionWrapper style={{width: "100%"}}>
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" marginBottom="1rem">
        <SectionHeading>Emails &amp; Text Messages</SectionHeading>
        <Box>
          <PrimaryButton
            onClick={() => {
                if (window.confirm(`Are you sure you want to resend ALL notifications? Please ensure all notifications are still relevant for the ${athlete}.`))
                  handleResendAll()
              }
            }>
            Resend All Messages
          </PrimaryButton>
        </Box>
      </Box>
      {/* Filter hidden - edit player page is scoped to specific player assessment
      {data?.communications && (
        <CommunicationFilter
          communications={data.communications}
          assessments={[]}
          ageGroups={[]}
          selectedAssessmentId={null}
          selectedAgeGroupId={null}
          onAssessmentChange={() => {}}
          onAgeGroupChange={() => {}}
        />
      )}
      */}
      <CommunicationList 
        data={data?.communications || []} 
        loading={loading} 
        resend={handleResendCommunication} 
        user={user}
      />
    </SectionWrapper>
  );
};
